import './OTP.scss';

import React, { useEffect } from 'react';
import { FormattedText, formatText } from 'core/i18n';
import { ONLY_ALPHANUMERIC_REGEX } from 'constants/regex';
import { useSplitInput } from 'componentsV2/SplitInput/useSplitInput';
import { SplitInput } from 'componentsV2/SplitInput/SplitInput';
import { OTP_LENGTH, MAX_OTP_SEND_ATTEMPTS } from './constants';
import { EmailOTPValidationActions } from '../../sections/EmailOTPValidation/EmailOTPValidationReducer';
import { EMAIL_OTP_VALIDATION_RESPONSE_STATUS } from '../../sections/constants';
import {
    doTagExpiredOTP,
    doTagErrorSnackBar,
    doTagSuccessfulSnackBar,
    doTagMaxRetrySendEmailOTPLimit,
    doTagIncorrectOTPLimit,
    doTagIncorrectOTP,
    doTagShowOTP,
    doTagSuccessfulOTP,
    doTagOTPResend,
    doTagOTPField,
    doTagOTPFieldRequired,
} from '../../sections/EmailOTPValidation/tagging';

const { OK, INCORRECT, INCORRECT_RETRY, INCORRECT_NO_RETRY, EXPIRED_RETRY, EXPIRED_NO_RETRY } =
    EMAIL_OTP_VALIDATION_RESPONSE_STATUS;

export const OTP = ({
    dispatch,
    errorMessage,
    isOTPValidated,
    preLoaderActions,
    readyCaptchaToken,
    retryOTPCounter,
    sendEmailOTP,
    validateOTP,
}) => {
    const {
        value: otpValue,
        isCompleted: isOTPCompleted,
        showCharacters: showOTPCharacters,
        onChange: onChangeSplitInput,
        resetValue: resetOTPValue,
    } = useSplitInput({
        howManyCharacters: OTP_LENGTH,
        defaultShowCharacters: true,
        alwaysUppercase: true,
    });

    useEffect(() => doTagShowOTP(), []);

    const resetSnackBar = () => {
        setTimeout(() => {
            dispatch({ type: EmailOTPValidationActions.RESET_SNACKBAR });
        }, 5000);
    };

    const handleOTPAction = async (recaptcha, successAction) => {
        preLoaderActions.showPreLoader();

        const response = await sendEmailOTP(recaptcha);

        setTimeout(() => {
            preLoaderActions.hidePreLoader();
        }, 500);

        resetOTPValue();
        resetSnackBar();

        if (response.status !== OK) {
            return dispatch({ type: EmailOTPValidationActions.GENERIC_ERROR });
        }

        return dispatch({ type: successAction });
    };

    const handleIncorrectRetry = recaptcha => {
        return handleOTPAction(recaptcha, EmailOTPValidationActions.OTP_INCORRECT_RETRY);
    };

    const handleExpiredRetry = recaptcha => {
        return handleOTPAction(recaptcha, EmailOTPValidationActions.OTP_EXPIRED_RETRY);
    };

    function handleValidateOTPResponse(status) {
        switch (status) {
            case OK: {
                doTagSuccessfulOTP();

                return dispatch({ type: EmailOTPValidationActions.VALIDATE_OTP });
            }
            case INCORRECT: {
                doTagIncorrectOTP();
                resetOTPValue();

                return dispatch({ type: EmailOTPValidationActions.OTP_INCORRECT });
            }
            case INCORRECT_RETRY: {
                doTagIncorrectOTPLimit();
                resetOTPValue();

                return readyCaptchaToken(handleIncorrectRetry);
            }
            case INCORRECT_NO_RETRY: {
                resetOTPValue();

                return dispatch({ type: EmailOTPValidationActions.OTP_INCORRECT_NO_RETRY });
            }
            case EXPIRED_RETRY: {
                doTagExpiredOTP();
                resetOTPValue();

                return readyCaptchaToken(handleExpiredRetry);
            }
            case EXPIRED_NO_RETRY: {
                resetOTPValue();

                return dispatch({ type: EmailOTPValidationActions.OTP_EXPIRED_NO_RETRY });
            }
            default: {
                return null;
            }
        }
    }

    const handleValidateOTP = async () => {
        preLoaderActions.showPreLoader();

        const response = await validateOTP(otpValue);

        setTimeout(() => {
            preLoaderActions.hidePreLoader();
        }, 500);

        handleValidateOTPResponse(response.status);
    };

    useEffect(() => {
        isOTPCompleted
            ? handleValidateOTP()
            : dispatch({
                  type: EmailOTPValidationActions.CHANGE_OTP_VALIDATION,
                  payload: { isOTPValidated: false },
              });
    }, [isOTPCompleted]);

    const handleValidateField = value => ONLY_ALPHANUMERIC_REGEX.test(value);

    const handleFocusIn = () => {
        doTagOTPField();
    };

    const handleFocusOut = () => {
        if (!isOTPCompleted && !errorMessage) {
            doTagOTPFieldRequired();
            dispatch({ type: EmailOTPValidationActions.FIELD_REQUIRED });
        }
    };

    const resetOTPStatus = () => {
        dispatch({ type: EmailOTPValidationActions.RESET_OTP_STATUS });
    };

    const handleOnChangeSplitInput = value => {
        resetOTPStatus();
        onChangeSplitInput(value);
    };

    const handleRetryOTP = async recaptcha => {
        if (retryOTPCounter >= MAX_OTP_SEND_ATTEMPTS) {
            doTagMaxRetrySendEmailOTPLimit();

            return dispatch({ type: EmailOTPValidationActions.RETRY_OTP_LIMIT });
        }

        preLoaderActions.showPreLoader();

        const response = await sendEmailOTP(recaptcha);

        setTimeout(() => {
            preLoaderActions.hidePreLoader();
        }, 500);

        resetOTPValue();
        resetSnackBar();

        if (response.status === OK) {
            doTagSuccessfulSnackBar();

            return dispatch({ type: EmailOTPValidationActions.RETRY_OTP_SUCCESS });
        }

        if (response.status === INCORRECT_RETRY || response.status === EXPIRED_RETRY) {
            doTagErrorSnackBar();

            return dispatch({ type: EmailOTPValidationActions.RETRY_OTP_FAILURE });
        }
    };

    const handleOnClick = () => {
        doTagOTPResend();
        readyCaptchaToken(handleRetryOTP);
    };

    return (
        <div className="otp__section__container">
            <div>
                <SplitInput
                    ariaLabel="otp-input"
                    autoComplete="off"
                    className="otp__section__container__input"
                    hasError={Boolean(errorMessage)}
                    howManyCharacters={OTP_LENGTH}
                    isCompleted={isOTPCompleted}
                    isDisabled={isOTPValidated}
                    isSuccess={isOTPValidated}
                    label={formatText('otpEmailValidation-enterCode')}
                    onChange={handleOnChangeSplitInput}
                    onFocusOut={handleFocusOut}
                    showCharacters={showOTPCharacters}
                    type="text"
                    validateField={handleValidateField}
                    value={otpValue}
                    onFocusIn={handleFocusIn}
                />
                {errorMessage && <p className="otp__section__container__error">{errorMessage}</p>}
            </div>
            {!isOTPValidated && (
                <div className="otp__section__retry">
                    <FormattedText value={'otpEmailValidation-checkSpamFolder'} />
                    <button className="otp__section__retry__button" onClick={handleOnClick} disabled={isOTPValidated}>
                        <FormattedText value={'otpEmailValidation-requestNewEmail'} />
                    </button>
                </div>
            )}
        </div>
    );
};
